/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { KeyCode, MESSAGE_BOX_ICON_VIEW, MessageBox, ThemeProvider, useModal } from '@veeam/components';
import * as themes from '@veeam/components/lib/themes';

import type { DialogAction, Theme, UseModalActionsProps } from '@veeam/components';
import type { CSSProperties, RefObject } from 'react';

import { NotificationDialogsTextKeys, NotificationDialogsIcons } from '../../../../services/notification';
import { questionIconSrc, successIconSrc } from '../../../../icons';
import { isReactComponent } from '../../../../helpers/react-helpers';
import { ParsedText } from '../../../ParsedText';

import type { ParsedTextConfig } from '../../../../helpers/text-parser';
import type { NotificationDialogsButton, NotificationDialogsMessage } from '../../../../services/notification';

export interface NotificationDialogsWrapperProps {
    title: string;
    text: NotificationDialogsMessage;
    buttons: NotificationDialogsButton[];
    icon?: NotificationDialogsIcons | string;
    theme?: Theme;
    width?: string;
    onFinish(type: NotificationDialogsTextKeys): void;
    dataTid?: string;
    onEnterNotificationDialogTextKey?: NotificationDialogsTextKeys;

    containerRef?: RefObject<HTMLElement>;

    canClose?: boolean;
    detailContent?: React.ReactElement;
    isDetailContentShown?: boolean;
    detailButtonText?: {
        show: string;
        hide: string;
    };
    pending?: boolean;
    loading?: boolean;
    style?: CSSProperties;
}

export class NotificationDialogsWrapper extends React.PureComponent<NotificationDialogsWrapperProps> {

    private dialogActions: UseModalActionsProps<unknown> | null = null;

    hideDialog(): void {
        if (this.dialogActions) {
            this.dialogActions.deactivate();
        }
    }

    showDialog(): void {
        if (this.dialogActions) {
            this.dialogActions.activate();
        }
    }

    render() {
        const { theme } = this.props;

        return (
            <ThemeProvider theme={theme || themes.blue}>
                <this.NotificationDialogsWrapperFunction {...this.props} />
            </ThemeProvider>
        );
    }

    private NotificationDialogsWrapperFunction = (props: NotificationDialogsWrapperProps) => {
        const {
            title,
            text,
            buttons,
            icon,
            width,
            canClose,
            containerRef,
            onFinish,
            dataTid,
            detailContent,
            isDetailContentShown,
            detailButtonText,
            pending,
            loading,
            style,
            onEnterNotificationDialogTextKey,
        } = props;
        const actions: DialogAction[] = buttons.map(({ type, text, view }: NotificationDialogsButton): DialogAction => ({
            text,
            view,
            onClick(): void {
                onFinish(type);
            },
            onKeyDown(event: React.KeyboardEvent): void {
                if (event.key === KeyCode[KeyCode.Enter]) {
                    onFinish(type);
                }
            },
        }));
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [dialog, dialogActions] = useModal({
            containerRef,
            render: () => (
                <MessageBox
                    header={title}
                    onRequestClose={() => onFinish(NotificationDialogsTextKeys.Close)}
                    onKeyPress={(event) => {
                        if (onEnterNotificationDialogTextKey && event.key === KeyCode[KeyCode.Enter]) {
                            onFinish(onEnterNotificationDialogTextKey);
                        }
                    }}
                    actions={actions}
                    canClose={canClose}
                    {...this.getIcon(icon)}
                    detailContent={detailContent}
                    isDetailContentShown={isDetailContentShown}
                    detailButtonText={detailButtonText}
                    pending={pending}
                    loading={loading}
                    style={{ width, ...style }}
                >
                    <div data-tid='NotificationDialogsWrapperText'>
                        {this.getTextContent(text)}
                    </div>
                </MessageBox>
            ),
        });

        this.dialogActions = dialogActions;

        return <div data-tid={dataTid || NotificationDialogsWrapper.name}>{dialog}</div>;
    };

    private getIcon(icon?: NotificationDialogsIcons | string): { iconView: MESSAGE_BOX_ICON_VIEW; } | { iconSrc: string; } | {} {
        if (!icon) {
            return {};
        } else if (Object.values(MESSAGE_BOX_ICON_VIEW).includes(icon as unknown as MESSAGE_BOX_ICON_VIEW)) {
            return { iconView: (icon as unknown as MESSAGE_BOX_ICON_VIEW) };
        } else if (icon === NotificationDialogsIcons.Alert) {
            return { iconView: MESSAGE_BOX_ICON_VIEW.warning };
        } else if (icon === NotificationDialogsIcons.Question) {
            return { iconSrc: questionIconSrc };
        } else if (icon === NotificationDialogsIcons.Success) {
            return { iconSrc: successIconSrc };
        }

        return { iconSrc: icon };
    }

    private getTextContent(text: NotificationDialogsMessage): React.ReactElement<void> {
        if (isReactComponent(text)) {
            return text as React.ReactElement<void>;
        }

        return <ParsedText textOrConfig={text as string | string[] | ParsedTextConfig[]} />;
    }

}
